import { Card, CardContent, CardHeader, Grid2, Typography, Step, Stepper, StepLabel, Button } from "@mui/material";
import React, { useState } from "react";
import Text from "../genericComponents/Text";
import { Box } from "@mui/system";
import { generatePDF } from "./generatePDF";
import OrganizationStep from "./steps/OrganizationStep";
import BuildingStep from "./steps/BuildingStep";
import MeterStep from "./steps/MeterStep";
import PaymentStep from "./steps/PaymentStep";
import ReviewStep from "./steps/ReviewStep";
import { gql, useMutation } from "@apollo/client";

const Onboarding = () => {
    const steps = ['Organization Info', 'Building Info', 'Select Meters', 'Review & Confirm', 'Payment Info'];
    const [activeStep, setActiveStep] = useState(0);
    const [organizationInfo, setOrganizationInfo] = useState({
        address: '',
        chamberOfCommerceNumber: '',
        contact: '',
        email: '',
        name: ''
    });

    // Each building has its own meters array
    const [buildings, setBuildings] = useState([
        {
            building: {
                addition: "",
                buildingYear: "",
                climateType: "",
                ean13: "",
                energyLabel: "",
                floorSize: "",
                isolationGrade: "",
                name: "",
                occupancyRate: "",
                organizationId: "00000000-0000-0000-0000-000000000000",
                postalCode: "",
                rental: "",
                streetNumber: "",
                usage: ""
            },
            meters: [
                {
                    buildingId: "00000000-0000-0000-0000-000000000000",
                    contractedCapacity: 0.0,
                    ean18: "",
                    externalDataProvider: "ANEXO",
                    gridOperator: "COTEQ",
                    highRate: 0.0,
                    lowRate: 0.0,
                    meterType: "ELECTRICITY",
                    name: ""
                }
            ]
        }
    ]);

    const [agreeConditions, setAgreeConditions] = useState(false);
    const [agreeMandate, setAgreeMandate] = useState(false);

    const [organizationErrors, setOrganizationErrors] = useState({});
    const [buildingErrors, setBuildingErrors] = useState([]);
    const [paymentUrl, setPaymentUrl] = useState("");
    const [paymentEans, setPaymentEans] = useState([]);

    const handleNext = () => {
        const isTesting = true; // Set to true to bypass validation checks for testing purposes

        if (!isTesting) {
            if (activeStep === 0 && !validateOrganizationInfo()) return;
            if (activeStep === 1 && !validateBuildingInfo()) return;
            if (activeStep === 3 && (!agreeConditions || !agreeMandate)) return;
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleOrganizationInputChange = (e) => {
        const { name, value } = e.target;
        setOrganizationInfo((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // Update a building's info by index
    const handleBuildingsInputChange = (idx, e) => {
        const { name, value } = e.target;
        setBuildings((prevBuildings) => {
            const updatedBuildings = [...prevBuildings];
            updatedBuildings[idx] = {
                ...updatedBuildings[idx],
                building: {
                    ...updatedBuildings[idx].building,
                    [name]: value
                }
            };
            return updatedBuildings;
        });
    };

    // Update meters for a building by index
    const handleMetersChange = (idx, meters) => {
        setBuildings((prevBuildings) => {
            const updatedBuildings = [...prevBuildings];
            updatedBuildings[idx] = {
                ...updatedBuildings[idx],
                meters
            };
            return updatedBuildings;
        });
    };

    // Update validation for organization
    const validateOrganizationInfo = () => {
        const errors = {};
        Object.entries(organizationInfo).forEach(([key, val]) => {
            if (!val.trim()) errors[key] = 'Required';
        });
        setOrganizationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Update validation for buildings
    const validateBuildingInfo = () => {
        if (buildings.length === 0) return false;
        const allErrors = buildings.map(b => {
            const errors = {};
            [
                'name', 'postalCode', 'streetNumber', 'ean13'
            ].forEach(field => {
                const value = b.building[field];
                if (typeof value === 'string' ? value.trim() === '' : value === '' || value == null) {
                    errors[field] = 'Required';
                }
            });
            return errors;
        });
        setBuildingErrors(allErrors);
        // Return true only if all buildings have no errors
        return allErrors.every(err => Object.keys(err).length === 0);
    };

    const logoBase64 = "data:image/png;base64,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";

    // GraphQL mutation for onboarding
    const ONBOARD_USER_MUTATION = gql`
        mutation OnBoardUser ($request: OnboardingRequestInput!) {
            onboardUser (request: $request) {
                isSuccess
                message
                value {
                    paymentUrl
                }
            }
        }
    `;

    const [onboardUser] = useMutation(ONBOARD_USER_MUTATION);

    const handleOnboardUser = async () => {
        // Prepare the organization object with correct field names
        const organizationPayload = {
            name: organizationInfo.name,
            address: organizationInfo.address,
            chamberOfCommerceNumber: organizationInfo.chamberOfCommerceNumber,
            contact: organizationInfo.contact,
            email: organizationInfo.email
        };

        // Flatten each building object if needed (not used in this mutation, but keep for future)
        const buildingsPayload = buildings.map(b => ({
            request: {
                addition: b.building.addition,
                buildingYear: b.building.buildingYear ,
                climateType: b.building.climateType,
                ean13: b.building.ean13,
                energyLabel: b.building.energyLabel,
                floorSize: b.building.floorSize,
                isolationGrade: b.building.isolationGrade,
                name: b.building.name,
                occupancyRate: b.building.occupancyRate,
                organizationId: "00000000-0000-0000-0000-000000000000",
                postalCode: b.building.postalCode,
                rental: b.building.rental,
                streetNumber: b.building.streetNumber,
                usage: b.building.usage,
            },
            meterRequests: b.meters.map(m => ({
                name: m.name,
                ean18: m.ean18,
                buildingId: "00000000-0000-0000-0000-000000000000",
                gridOperator: m.gridOperator,
                meterType: m.meterType,
                lowRate: m.lowRate,
                highRate: m.highRate,
                contractedCapacity: m.contractedCapacity,
                externalDataProvider: m.externalDataProvider
            }))
        }));

        console.log('Onboarding user with data:', {
            organization: organizationPayload,
            buildings: buildingsPayload
        });

        try {
            // generatePDF({
            //     logoBase64,
            //     organizationPayload,
            //     buildingsPayload,
            //     agreeConditions,
            //     agreeMandate,
            // });

            const { data } = await onboardUser({
                variables: {
                    request: {
                        organization: organizationPayload,
                        buildings: buildingsPayload,
                    }
                }
            });

            // If paymentUrl is returned, collect all EANs and go to PaymentStep
            if (data.onboardUser.value.paymentUrl) {
                // Collect all EANs from buildingsPayload
                const eans = [];
                buildingsPayload.forEach(b =>
                    b.meterRequests.forEach(m => {
                        if (m.ean18) eans.push(m.ean18);
                    })
                );
                setPaymentUrl(data.onboardUser.value.paymentUrl);
                setPaymentEans(eans);
                setActiveStep(4); // Go to PaymentStep
            } else if (data.onboardUser.message) {
                alert(data.onboardUser.message);
            }
        } catch (error) {
            console.error('Onboarding mutation failed', error);
        }
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid2 container spacing={2}>
                <Card style={{ height: "100%", marginBottom: 20, width: "100%" }}>
                    <CardHeader
                        sx={{ height: 61, borderBottom: "1px solid #ddd" }}
                        title={
                            <Grid2 container alignItems="center">
                                <Text sx={{ pl: 1 }} variant="h2" text="Onboarding" />
                                <Box sx={{ flexGrow: 1 }} />
                            </Grid2>
                        } />
                    <CardContent sx={{ flexGrow: 1, alignContent: "center", justifyItems: "center" }}>
                        <Box sx={{ width: '100%' }}>
                            <Stepper activeStep={activeStep}>
                                {steps.map((label, index) => (
                                    <Step key={index}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {activeStep === steps.length ? (
                                <React.Fragment>
                                    <Typography sx={{ mt: 2, mb: 1 }} variant="h5">
                                        Congratulations!
                                    </Typography>
                                    <Typography sx={{ mt: 2, mb: 1 }}>
                                        You have successfully completed the onboarding process.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={generatePDF}
                                        sx={{ mt: 2 }}
                                    >
                                        Download Summary as PDF
                                    </Button>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                        <Box sx={{ flex: '1 1 auto' }} />
                                        <Button
                                            variant="contained"
                                            onClick={handleReset}
                                            sx={{ mt: 2 }}
                                        >
                                            Reset
                                        </Button>
                                    </Box>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {activeStep === 0 && (
                                        <OrganizationStep
                                            organizationInfo={organizationInfo}
                                            handleInputChange={handleOrganizationInputChange}
                                            errors={organizationErrors}
                                        />
                                    )}
                                    {activeStep === 1 && (
                                        <BuildingStep
                                            buildings={buildings}
                                            setBuildings={setBuildings}
                                            handleBuildingsInputChange={handleBuildingsInputChange}
                                            errors={buildingErrors}
                                        />
                                    )}
                                    {activeStep === 2 && (
                                        <MeterStep
                                            buildings={buildings}
                                            setMetersForBuilding={handleMetersChange}
                                        />
                                    )}
                                    {activeStep === 3 && (
                                        <ReviewStep
                                            organizationInfo={organizationInfo}
                                            buildings={buildings}
                                            agreeConditions={agreeConditions}
                                            setAgreeConditions={setAgreeConditions}
                                            agreeMandate={agreeMandate}
                                            setAgreeMandate={setAgreeMandate}
                                        />
                                    )}
                                    {activeStep === 4 && (
                                        <PaymentStep
                                            paymentUrl={paymentUrl}
                                            eans={paymentEans}
                                        />
                                    )}
                                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleBack}
                                            disabled={activeStep === 0}
                                            sx={{ mr: 2 }}
                                        >
                                            Back
                                        </Button>
                                        <Box sx={{ flex: '1 1 auto' }} />
                                        <Button
                                            variant="contained"
                                            onClick={
                                                activeStep === 3 && agreeConditions && agreeMandate
                                                    ? handleOnboardUser
                                                    : handleNext
                                            }
                                            disabled={
                                                activeStep === steps.length - 1 && (!agreeConditions || !agreeMandate)
                                            }
                                            sx={{ mt: 2 }}
                                        >
                                            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                        </Button>
                                    </Box>
                                </React.Fragment>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid2>
        </Box>
    );
};

export default Onboarding;
